import React, { useContext } from 'react';

import Section from '../../../../../common/data-display/Section/Section';
import Button from '../../../../../common/general/Button';
import ButtonGroup from '../../../../../common/general/ButtonGroup';
import { MANAGE_BUDGETS } from '../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../services/auth';
import { PageInfoHeader } from '../../../../PageInfoHeader/PageInfoHeader';
import CommentSection from '../../Protocol/ProtocolSetup/CommentSection/CommentSection';
import CommentSectionForPublishedProtocolAndBudget from '../../Protocol/ProtocolSetup/CommentSection/CommentSectionForPublishedProtocolAndBudget';

import { BudgetDetailsTableSection } from './BudgetDetailsTableSection/BudgetDetailsTableSection';
import { BudgetDetailsContext, withBudgetDetailsContext } from './BudgetDetailsContext';

import './BudgetDetails.scss';

export const BudgetDetails = () => {
  const {
    state: { budget, isEditingForbidden, currentStudyBudget, encounterTriggers, itemGroupTriggers, vendors, events },
    updateStudyBudget,
    allRequiredFieldsAreFilledCorrectly,
    exportStudyBudget,
    onChangeTextVal,
    onBlur,
    onBlurManagementFee,
    budgetUniqueIdentifier,
    loadBudgetEvents,
    protocolIncomplete
  } = useContext(BudgetDetailsContext);

  return (
    <div className="budget-details-page">
      <PageInfoHeader
        objectRecordLabel={budget?.description}
        pageInfo={
          <PageInfoHeader.CollapsibleList>
            <PageInfoHeader.AdditionalInfo tooltip="Study">{budget?.study.name}</PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip="Client">{budget?.finClient.name}</PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip="Version">{budget?.version}</PageInfoHeader.AdditionalInfo>
          </PageInfoHeader.CollapsibleList>
        }
        right={
          <ButtonGroup classNames="bd-buttons">
            <Button type="button" size="h28" to={`optional-ig-setup`} id="gotoOptionalIGSetup">
              Exclude
            </Button>
            {!isEditingForbidden && userHasAccessTo(MANAGE_BUDGETS) && (
              <Button
                type="button"
                size="h28"
                id="updateBudgetBtn"
                disabled={!allRequiredFieldsAreFilledCorrectly()}
                onClick={updateStudyBudget}
                data-testid="update-budget-button"
              >
                Update
              </Button>
            )}
            <Button priority="medium" size="h28" type="button" id="exportBudgetBtn" onClick={exportStudyBudget}>
              Export
            </Button>
          </ButtonGroup>
        }
      />
      <Section>
        <div className="budget-detail-top">
          <div className="row">
            <div className="col-md-5">
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <input
                  id="description"
                  className="form-control reqfeild"
                  type="text"
                  value={budget?.description}
                  name="description"
                  disabled={isEditingForbidden || !userHasAccessTo(MANAGE_BUDGETS)}
                  onChange={onChangeTextVal}
                  onBlur={onBlur}
                />
              </div>
            </div>
            <div className="col-md-2">
              <div className="form-group">
                <label htmlFor="overhead">Overhead</label>
                <div className="input-group">
                  <input
                    id="overhead"
                    type="number"
                    className="form-control reqfeild"
                    value={budget?.overhead}
                    name="overhead"
                    disabled={isEditingForbidden || !userHasAccessTo(MANAGE_BUDGETS)}
                    onChange={onChangeTextVal}
                    onBlur={onBlur}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">%</span>
                  </div>
                </div>
                <small id="overheadHelp" className="form-text text-muted">
                  Range: 0.0 - 100.0
                </small>
              </div>
            </div>
            <div className="col-md-2">
              <div className="form-group">
                <label htmlFor="withholding">Withholding</label>
                <div className="input-group">
                  <input
                    id="withholding"
                    type="number"
                    className="form-control reqfeild"
                    value={budget?.withholding}
                    name="withholding"
                    disabled={isEditingForbidden || !userHasAccessTo(MANAGE_BUDGETS)}
                    onChange={onChangeTextVal}
                    onBlur={onBlur}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">%</span>
                  </div>
                </div>
                <small id="withholdingHelp" className="form-text text-muted">
                  Range: 0.0 - 100.0
                </small>
              </div>
            </div>
            <div className="col-md-2">
              <div className="form-group">
                <label htmlFor="managementFee">Management Fee</label>
                <div className="input-group">
                  <input
                    id="managementFee"
                    type="number"
                    className="form-control reqfeild"
                    value={budget?.managementFee}
                    name="managementFee"
                    disabled={isEditingForbidden || !userHasAccessTo(MANAGE_BUDGETS)}
                    onChange={onChangeTextVal}
                    onBlur={onBlurManagementFee}
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">%</span>
                  </div>
                </div>
                <small id="managementFeeHelp" className="form-text text-muted">
                  Range: 0.0 - 100.0
                </small>
              </div>
            </div>
          </div>
        </div>
      </Section>
      {isEditingForbidden ? (
        <CommentSectionForPublishedProtocolAndBudget comment={budget?.comment} />
      ) : (
        <CommentSection
          name="comment"
          onChange={onChangeTextVal}
          onBlur={onBlur}
          value={budget?.comment}
          disabled={isEditingForbidden || !userHasAccessTo(MANAGE_BUDGETS)}
        />
      )}
      <BudgetDetailsTableSection
        studyId={budget?.studyId}
        managementFeePercent={budget?.managementFee}
        currentStudyBudget={currentStudyBudget}
        encounterTriggers={encounterTriggers}
        itemGroupTriggers={itemGroupTriggers}
        vendors={vendors}
        budgetId={budgetUniqueIdentifier}
        loadBudgetEvents={loadBudgetEvents}
        protocolIncomplete={protocolIncomplete}
        isEditingForbidden={isEditingForbidden}
        events={events}
      />
    </div>
  );
};

export default withBudgetDetailsContext(BudgetDetails);
